import { cn } from '@/utils';
import { SkeletonLoader } from '@/components/Loaders/SkeletonLoader';
import Image from 'next/image';
import React, { useState } from 'react';

export interface AppImageProps extends React.ComponentProps<typeof Image> {
  height: number;
  width: number;
  src: string;

  /**
   * Fallback image source to use if the main image fails to load
   */
  fallbackSrc?: string;

  /**
   * Additional class name to apply to the fallback image when the main image fails to load
   */
  fallbackClassName?: string;

  /**
   * Whether to show a skeleton loader while the image is loading
   * @default true
   */
  showSkeleton?: boolean;

  /**
   * Rounding size for the skeleton loader
   */
  skeletonRounding?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

  /**
   * Whether to show a flat <img> tag for printing purposes
   * @default false
   */
  isPrinting?: boolean;
}

/**
 * AppImage component that extends NextJS's `<Image>` for
 * rendering images with enhanced source path handling and skeleton loading.
 */
export const AppImage: React.FC<AppImageProps> = ({
  alt = '',
  className,
  height,
  width,
  src,
  fallbackSrc,
  fallbackClassName,
  showSkeleton = true,
  skeletonRounding = 'md',
  isPrinting = false,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  /**
   * Determine the fallback image source, which will be used if the main image fails to load.
   */
  const fallbackImageSource = fallbackSrc?.includes('/public')
    ? fallbackSrc.replace('/public', '')
    : fallbackSrc || '';

  /**
   * Remove '/public' from the image source path if present; allows a developer
   * to still use editor intellisense to locate images in the public directory,
   * yet still render correctly in the application.
   */
  const imageSource = src.includes('/public') ? src.replace('/public', '') : src;

  /**
   * Handle image load completion by updating the loading state, which will
   * hide the skeleton loader.
   */
  const handleLoadComplete = () => {
    setIsLoading(false);
  };

  /**
   * Handle image load error by updating the loading and error states, which will
   * hide the skeleton loader and display an error state if needed.
   */
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div className="pointer-events-none relative" style={{ width, height }}>
      {showSkeleton && isLoading && !hasError && (
        <SkeletonLoader
          height="full"
          width="full"
          rounded={skeletonRounding}
          className="absolute inset-0"
        />
      )}

      {isPrinting ? (
        // No need for next/image in print mode
        // eslint-disable-next-line @next/next/no-img-element
        <img
          alt={alt}
          className={cn(className, hasError && fallbackClassName)}
          src={hasError ? fallbackImageSource : imageSource}
        />
      ) : (
        <Image
          className={cn(
            className,
            hasError && fallbackClassName,
            isLoading && showSkeleton ? 'opacity-0' : 'opacity-100',
            'transition-opacity duration-200',
          )}
          alt={alt}
          src={hasError ? fallbackImageSource : imageSource}
          height={height}
          width={width}
          onLoad={handleLoadComplete}
          onError={handleError}
          {...props}
        />
      )}
    </div>
  );
};

AppImage.displayName = 'AppImage';

export default AppImage;
